import { createContext, useContext } from 'react'
import { CalendarCheck, UserCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getDueDateDisplayInfo } from '../utils/datetime'
import { countRangeDays, isMultiDayRange, orderRange } from '../utils/dateRange'
import { DateDurationPicker } from './DateDurationPicker'
import { PriorityComboBox } from './PriorityComboBox'
import { EstimationPicker } from './EstimationPicker'
import { AssigneeComboBox } from './AssigneeComboBox'
import { ProjectComboBox } from './ProjectComboBox'
import type { ReactNode } from 'react'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import type { EstimationType } from '@/lib/types/estimation'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { WorkItemTypePicker } from '@/components/app/tasks/WorkItemTypePicker'
import { cn } from '@/lib/utils'
import { useLocale } from '@/lib/i18n/LocaleProvider'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'

type MetadataLayout = 'horizontal' | 'sidebar' | 'grid'

/** `tiles`: phones (design 36b) — two tiles per row, label over value. */
export type GridDensity = 'column' | 'stacked' | 'tiles'
const GridDensityContext = createContext<GridDensity>('column')

/**
 * One property. `horizontal` wraps it in a bordered chip, `sidebar` leaves it
 * bare, and `grid` (design 6a) shows a quiet label/value row.
 */
function PropertySlot({
  layout,
  label,
  dashed,
  className,
  children,
}: {
  layout: MetadataLayout
  label: string
  dashed?: boolean
  className?: string
  children: ReactNode
}) {
  const density = useContext(GridDensityContext)
  if (layout === 'grid' && density === 'tiles') {
    return (
      <div className={cn('flex min-w-0 flex-col gap-0.5 rounded-lg bg-secondary/60 px-3 pb-1.5 pt-2', className)}>
        <span className="text-[11.5px] text-muted-foreground">{label}</span>
        <div className="min-w-0 [&_button]:h-8">{children}</div>
      </div>
    )
  }
  if (layout === 'grid') {
    // Design 30: a right column uses 76px labels and 32px rows; a grid
    // stacked under the title uses 70px labels and 30px rows.
    return (
      <div className={cn('flex min-w-0 items-center gap-3', density === 'column' ? 'min-h-8' : 'min-h-[30px]', className)}>
        <span className={cn('shrink-0 text-[13px] text-muted-foreground', density === 'column' ? 'w-[76px]' : 'w-[70px]')}>
          {label}
        </span>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    )
  }
  return (
    <div
      className={cn(
        layout === 'horizontal' &&
          'inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 shadow-sm',
        layout === 'horizontal' && dashed && 'border-dashed',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** Trigger button styles per layout; grid triggers are borderless and hover-tinted. */
function triggerClass(layout: MetadataLayout, muted = false): string {
  if (layout === 'sidebar') {
    return cn(
      'w-full justify-start border-0 px-0 py-1 bg-transparent hover:bg-sidebar-accent text-sm shadow-none',
      muted ? 'text-sidebar-foreground/80' : 'text-sidebar-foreground',
    )
  }
  if (layout === 'grid') {
    return cn(
      'h-7 -ml-2 max-w-full justify-start gap-1.5 rounded-md border-0 bg-transparent px-2 text-[13.5px] font-normal shadow-none hover:bg-secondary focus:bg-secondary dark:bg-transparent dark:hover:bg-secondary',
      muted ? 'text-muted-foreground' : 'text-foreground',
    )
  }
  return cn(
    'h-auto border-0 bg-transparent shadow-none p-0 text-sm font-medium hover:bg-transparent focus:bg-transparent transition-colors',
    muted ? 'text-muted-foreground' : 'text-foreground',
  )
}

interface TaskMetadataSectionProps {
  layout: MetadataLayout
  /** Grid only: status control rendered as the first row. */
  /** Grid layout only: two columns (side panel, full page) or one (modal column). */
  gridColumns?: 1 | 2
  /** Grid layout: right-column or stacked sizing (design 30). */
  gridDensity?: GridDensity
  /** Grid layout: the Cycle row, between Project and Labels (design 30). */
  cycleSlot?: ReactNode
  statusSlot?: ReactNode
  /** Grid only: labels control rendered as the last row. */
  labelsSlot?: ReactNode
  startDate: Date | undefined
  dueDate: Date | undefined
  onStartDateChange: (date: Date | undefined) => void
  onDueDateChange: (date: Date | undefined) => void
  reminderOffsetMinutes: number | null
  onReminderChange: (
    offset: number | null,
    nextScheduledAt: number | null,
    recurrencePattern?: RecurrencePattern | null,
  ) => void
  recurrencePattern: RecurrencePattern | null
  onRecurrenceChange: (pattern: RecurrencePattern | null) => void
  taskCreatedOn?: number | null
  priorityValue: number
  onPriorityChange: (priority: number) => void
  userIdValue: string | null
  onUserChange: (userId: string | null) => void
  accountableUserIdValue?: string | null
  onAccountableUserChange?: (userId: string | null) => void
  projects: Array<ProjectDocument>
  selectedProjectId: string | null
  onProjectChange: (projectId: string | null) => void
  isSomeday: boolean
  onSomedayChange: (nextValue: boolean) => void
  isAllDay?: boolean
  onIsAllDayChange?: (isAllDay: boolean) => void
  priorityPopoverOpen?: boolean
  onPriorityPopoverOpenChange?: (open: boolean) => void
  assigneePopoverOpen?: boolean
  onAssigneePopoverOpenChange?: (open: boolean) => void
  assigneeShortcutHint?: string | Array<string> | null
  estimationValue: number | null
  onEstimationChange: (value: number | null) => void
  estimationType: EstimationType
  projectPopoverOpen?: boolean
  onProjectPopoverOpenChange?: (open: boolean) => void
  projectShortcutHint?: string | Array<string> | null
  workItemTypeId?: string | null
  onWorkItemTypeChange?: (typeId: string) => void
}

export function TaskMetadataSection({
  layout,
  gridColumns = 2,
  gridDensity = 'column',
  cycleSlot,
  statusSlot,
  labelsSlot,
  startDate,
  dueDate,
  onStartDateChange,
  onDueDateChange,
  reminderOffsetMinutes,
  onReminderChange,
  recurrencePattern,
  onRecurrenceChange,
  taskCreatedOn,
  priorityValue,
  onPriorityChange,
  userIdValue,
  onUserChange,
  accountableUserIdValue = null,
  onAccountableUserChange,
  projects,
  selectedProjectId,
  onProjectChange,
  isSomeday,
  onSomedayChange,
  isAllDay,
  onIsAllDayChange,
  priorityPopoverOpen,
  onPriorityPopoverOpenChange,
  assigneePopoverOpen,
  onAssigneePopoverOpenChange,
  assigneeShortcutHint,
  estimationValue,
  onEstimationChange,
  estimationType,
  projectPopoverOpen,
  onProjectPopoverOpenChange,
  projectShortcutHint,
  workItemTypeId,
  onWorkItemTypeChange,
}: TaskMetadataSectionProps) {
  const { t } = useTranslation('tasks')
  const { locale, is24Hour } = useLocale()
  const { isPersonal } = useWorkspaceMode()
  const selectedProject = selectedProjectId
    ? projects.find((p) => p.id === selectedProjectId)
    : null
  const showAccountable =
    !isPersonal
    && (selectedProject?.isAccountableEnabled ?? false)
    && typeof onAccountableUserChange === 'function'

  const formatShortcut = (hint?: string | Array<string> | null) => {
    if (!hint) return ''
    return Array.isArray(hint) ? hint.join(' + ') : hint
  }

  const assigneeTriggerProps = assigneeShortcutHint
    ? { title: `Assignee (${formatShortcut(assigneeShortcutHint)})` }
    : undefined
  const projectTriggerProps = projectShortcutHint
    ? { title: `Project (${formatShortcut(projectShortcutHint)})` }
    : undefined

  // A range reads "Sep 28 → Oct 2  5d" on the property row (design 31b).
  const rangeDays =
    startDate && dueDate && isMultiDayRange(startDate, dueDate)
      ? countRangeDays(orderRange(startDate, dueDate))
      : null

  // Color-coded display info
  const displayInfo = getDueDateDisplayInfo(dueDate, startDate, isSomeday, undefined, { locale, is24Hour })

  const isGrid = layout === 'grid'

  const dateTrigger =
    layout === 'sidebar' || isGrid ? (
      <button
        type="button"
        className={cn(
          isGrid
            ? cn(triggerClass('grid'), 'inline-flex items-center')
            : 'w-full inline-flex items-center justify-start gap-2 px-0 py-1 text-sm hover:bg-sidebar-accent transition rounded text-sidebar-foreground',
        )}
      >
        <CalendarCheck
          className={cn(
            'h-4 w-4 shrink-0',
            displayInfo.colorClass || (isGrid ? 'text-muted-foreground' : 'text-sidebar-foreground/60'),
          )}
        />
        <span className={cn('truncate', isGrid && !dueDate && !startDate && !isSomeday && 'text-muted-foreground')}>
          {displayInfo.label}
        </span>
        {rangeDays !== null && (
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
            {t('common:picker.date.range.daysShort', { count: rangeDays })}
          </span>
        )}
      </button>
    ) : undefined

  const content = (
    <>
      {isGrid && statusSlot && (
        <PropertySlot layout={layout} label={t('properties.status')}>
          {statusSlot}
        </PropertySlot>
      )}

      {onWorkItemTypeChange && (
        <PropertySlot layout={layout} label={t('properties.type')}>
          <WorkItemTypePicker
            currentTypeId={workItemTypeId}
            onTypeChange={onWorkItemTypeChange}
            buttonClassName={triggerClass(layout)}
          />
        </PropertySlot>
      )}

      <PropertySlot layout={layout} label={t('properties.priority')}>
        <PriorityComboBox
          selectedPriority={priorityValue}
          onPriorityChange={onPriorityChange}
          buttonClassName={triggerClass(layout)}
          fontSize="sm"
          popoverOpen={priorityPopoverOpen}
          onPopoverOpenChange={onPriorityPopoverOpenChange}
        />
      </PropertySlot>

      {!isPersonal && (
        <PropertySlot layout={layout} label={t('properties.assignee')}>
          <AssigneeComboBox
            projectId={selectedProject?.id ?? null}
            selectedUserId={userIdValue}
            onUserChange={onUserChange}
            allowSuperAgent={selectedProject?.superAgentEnabled ?? false}
            buttonClassName={triggerClass(layout)}
            fontSize="sm"
            popoverOpen={assigneePopoverOpen}
            onPopoverOpenChange={onAssigneePopoverOpenChange}
            searchShortcutHint={assigneeShortcutHint ?? undefined}
            triggerProps={assigneeTriggerProps}
          />
        </PropertySlot>
      )}

      <PropertySlot layout={layout} label={t('properties.due')}>
        <DateDurationPicker
          startDate={startDate}
          dueDate={dueDate}
          onStartDateChange={onStartDateChange}
          onDueDateChange={onDueDateChange}
          isSomeday={isSomeday}
          onSomedayChange={onSomedayChange}
          isAllDay={isAllDay}
          onIsAllDayChange={onIsAllDayChange}
          reminderOffsetMinutes={reminderOffsetMinutes}
          onReminderChange={onReminderChange}
          recurrencePattern={recurrencePattern}
          onRecurrenceChange={onRecurrenceChange}
          taskCreatedOn={taskCreatedOn}
          trigger={dateTrigger}
        />
      </PropertySlot>

      {estimationType !== 'none' && (
        <PropertySlot layout={layout} label={t('properties.estimate')}>
          <EstimationPicker
            value={estimationValue}
            onChange={onEstimationChange}
            estimationType={estimationType}
            buttonClassName={triggerClass(layout)}
          />
        </PropertySlot>
      )}

      {showAccountable && (
        <PropertySlot layout={layout} label={t('properties.owner')} dashed>
          <AssigneeComboBox
            projectId={selectedProject?.id ?? null}
            selectedUserId={accountableUserIdValue}
            onUserChange={(value) => onAccountableUserChange?.(value)}
            allowSuperAgent={selectedProject?.superAgentEnabled ?? false}
            placeholderText="Owner"
            noneLabel="No owner"
            Icon={UserCheck}
            buttonClassName={triggerClass(layout, true)}
            fontSize="sm"
            triggerProps={{ title: 'Owner (secondary user for oversight)' }}
          />
        </PropertySlot>
      )}

      {projects.length > 0 && (
        <PropertySlot
          layout={layout}
          label={t('properties.project')}
          className={layout === 'sidebar' ? 'space-y-2 pt-3' : undefined}
        >
          {layout === 'sidebar' && (
            <h3 className="text-xs font-semibold text-sidebar-foreground/60 uppercase tracking-wide">
              Project
            </h3>
          )}
          <ProjectComboBox
            projects={projects}
            selectedProjectId={selectedProjectId}
            onProjectChange={(projectId) => {
              onProjectChange(projectId || null)
            }}
            buttonClassName={triggerClass(layout)}
            fontSize="sm"
            popoverOpen={projectPopoverOpen}
            onPopoverOpenChange={onProjectPopoverOpenChange}
            searchShortcutHint={projectShortcutHint ?? undefined}
            triggerProps={projectTriggerProps}
          />
        </PropertySlot>
      )}

      {isGrid && cycleSlot && (
        <PropertySlot layout={layout} label={t('properties.cycle')}>
          {cycleSlot}
        </PropertySlot>
      )}

      {isGrid && labelsSlot && (
        <PropertySlot layout={layout} label={t('properties.labels')}>
          {labelsSlot}
        </PropertySlot>
      )}
    </>
  )

  if (isGrid) {
    return (
      <GridDensityContext.Provider value={gridDensity}>
        <div
          className={cn(
            'grid',
            gridDensity === 'tiles' ? 'gap-2' : 'gap-x-8 gap-y-0.5',
            gridColumns === 2 && 'grid-cols-2',
          )}
        >
          {content}
        </div>
      </GridDensityContext.Provider>
    )
  }
  return content
}
